// ============================================================================
// src/pig/pigFeederOps.ts — running a 231 feeder operation against the
// route's local copy of ppp-feeders-v1
// ----------------------------------------------------------------------------
// The batch hooks (sub-batches, mortality, the feeder form) used to rebuild the
// whole feeder document and hand it to `persistFeeders`. They now send ONE
// narrow operation through src/lib/pigOperationsApi.ts. This module is the
// seam between that call and the route's state:
//
//   * operations run on the SAME promise chain as the remaining whole-document
//     writes (planned-trip allocation, processing trips — B4c), so a local
//     whole-document save can never interleave with an operation and resurrect
//     what the operation just changed;
//   * a live result's `rows` are adopted into the local copy at once (the
//     server's element, not the client's guess);
//   * a replay, an unknown outcome or a conflict re-reads the document — a
//     receipt holds no rows, and a conflict means the local copy is stale.
// ============================================================================

import type {PigFeederGroup} from '../lib/pig.js';
import {applyPigOperation, type PigOpOutcome, type PigOpRequest} from '../lib/pigOperationsApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {SaveStatusSink} from '../lib/appStoreBlobApi.js';

const sameId = (a: unknown, b: unknown) => typeof a === 'string' && typeof b === 'string' && a.trim() === b.trim();

/**
 * The local feeder list with a live result's rows applied, or null when the
 * result carries nothing to adopt. `rows.feeder` replaces (or adds) that batch;
 * `rows.feeder_id` alone means it was deleted.
 */
export function adoptFeederRows(
  groups: PigFeederGroup[],
  rows: Record<string, unknown> | null | undefined,
): PigFeederGroup[] | null {
  if (!rows) return null;
  const feeder = rows.feeder;
  if (feeder && typeof feeder === 'object' && !Array.isArray(feeder)) {
    const next = feeder as PigFeederGroup;
    const at = groups.findIndex((g) => sameId(g.id, next.id));
    if (at < 0) return [...groups, next];
    const copy = groups.slice();
    copy[at] = next;
    return copy;
  }
  if (typeof rows.feeder_id === 'string') {
    const kept = groups.filter((g) => !sameId(g.id, rows.feeder_id));
    return kept.length === groups.length ? null : kept;
  }
  return null;
}

export type FeederOpRunnerDeps = {
  sb: SupabaseClient;
  /** The chain the route's whole-document writes also run on. */
  chain: {current: Promise<unknown>};
  getGroups: () => PigFeederGroup[];
  setGroups: (next: PigFeederGroup[]) => void;
  reread: () => void;
  onSaveStatus: SaveStatusSink;
};

export type FeederOpRunner = (request: PigOpRequest) => Promise<PigOpOutcome>;

export function createFeederOpRunner(deps: FeederOpRunnerDeps): FeederOpRunner {
  return (request) => {
    const run = deps.chain.current.then(async () => {
      deps.onSaveStatus('saving');
      const outcome = await applyPigOperation(deps.sb, request);
      if (outcome.status === 'saved' || outcome.status === 'no_change' || outcome.status === 'conflict') {
        const adopted = adoptFeederRows(deps.getGroups(), outcome.rows);
        if (adopted) deps.setGroups(adopted);
      }
      const reread =
        outcome.status === 'unknown' ||
        outcome.status === 'conflict' ||
        (outcome.status === 'saved' && outcome.mustReread);
      if (reread) deps.reread();
      const ok = outcome.status === 'saved' || outcome.status === 'no_change';
      deps.onSaveStatus(ok ? 'saved' : 'error');
      setTimeout(() => deps.onSaveStatus(''), ok ? 2500 : 4000);
      return outcome;
    });
    deps.chain.current = run.catch(() => undefined);
    return run;
  };
}
